

































































































































































































































/* eslint-disable @typescript-eslint/explicit-member-accessibility, @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any */
import { Vue, Watch } from 'vue-property-decorator'
import Component from 'vue-class-component'
import PageTab from '@/components/ui/PageTab.vue'
import { Debounce } from 'lodash-decorators'
import { vxm } from '@/store'

type Shop = {
  id: number
  title: string
}

type Brand = {
  id: number
  name: string
}

type SupplierData = {
  id: number
  name: string
}

type Supplier = {
  id: number
  name: string
}

type ColumnConfig = {
  label1: string
  label2: string
  placeholder: string
  rank: {
    [key: string]: number[]
  }
  brandSuppliers: {
    [key: string]: number[] // key format: `${rank}-${brandId}`
  }
}

@Component({
  components: {
    PageTab,
  },
})
export default class TopBrandsConfig extends Vue {
  $refs: Vue['$refs'] & {
    brandsConfigForm: {
      validate: any
      reset: any
    }
  }

  // Constants for maximum limits
  private readonly MAX_RANKS = 12
  private readonly MAX_BRANDS_PER_RANK = 12
  private readonly MAX_SUPPLIERS = 12

  private loading = true
  private configLoading = false
  private saveLoading = false

  private shops: Shop[] = []
  private brands: Brand[] = []
  private suppliers: Supplier[] = []
  private selectedShopId: number | null = null
  private includeBrands: number[] = []
  private excludeBrands: number[] = []

  private brandsConfig: { [key: number]: ColumnConfig } = {
    1: { label1: '', label2: '', placeholder: '', rank: {}, brandSuppliers: {} },
    2: { label1: '', label2: '', placeholder: '', rank: {}, brandSuppliers: {} },
    3: { label1: '', label2: '', placeholder: '', rank: {}, brandSuppliers: {} },
  }

  private selectedBrand: { [key: number]: number | null } = {
    1: null,
    2: null,
    3: null,
  }

  private selectedRank: { [key: number]: string } = {
    1: '1',
    2: '1',
    3: '1',
  }

  private selectedSupplier: { [key: string]: number | null } = {}

  private rules = {
    shopId: [(v: any) => !!v || this.$t('c:validation:This field is required')],
  }

  private async mounted() {
    try {
      const shopsResponse = await this.$axios.get('/v4/site/get-webshops')
      this.shops = shopsResponse.data.data.map((shop: any) => ({
        ...shop,
      }))

      // Check if there's a webshop_id in the URL
      const webshopId = this.$route.params.id
      if (webshopId) {
        this.selectedShopId = parseInt(webshopId)
        this.loadShopConfig()
      }

      this.loading = false
    } catch (err) {
      await vxm.alert.onAxiosError(err, 'Error loading page')
    }
  }

  // Handle shop selection change
  private onShopChange() {
    if (this.selectedShopId) {
      // Update URL with shop ID
      this.$router.push({
        name: 'Admin/WebShop/TopBrandsConfig',
        params: { id: String(this.selectedShopId) },
      })
      this.loadShopConfig()
    } else {
      this.$router.push({
        name: 'Admin/WebShop/TopBrandsConfig',
      })
    }
  }

  private resetConfig() {
    this.brandsConfig = {
      1: { label1: '', label2: '', placeholder: '', rank: {}, brandSuppliers: {} },
      2: { label1: '', label2: '', placeholder: '', rank: {}, brandSuppliers: {} },
      3: { label1: '', label2: '', placeholder: '', rank: {}, brandSuppliers: {} },
    }
    this.selectedSupplier = {}
  }

  private async loadShopConfig() {
    if (!this.selectedShopId) {
      this.resetConfig()
      return
    }

    this.configLoading = true
    try {
      const response = await this.$axios.get(`/v4/site/webshop/top-brands-config/${this.selectedShopId}`)
      if (response.data.data) {
        const configData =
          typeof response.data.data.topBrandsConfig === 'string'
            ? JSON.parse(response.data.data.topBrandsConfig)
            : response.data.data.topBrandsConfig

        this.includeBrands = response.data.data.includeBrands
        this.excludeBrands = response.data.data.excludeBrands
        this.brands = response.data.data.brands

        // Transform suppliers array to the format we need
        this.suppliers = (response.data.data.suppliers || []).map((supplierData: any) => ({
          id: supplierData.id,
          name: supplierData.name,
        }))

        // Add "Local" supplier with id=0 at the top of the list
        const { newSearch, shopSupplierId } = response.data.data
        if (!newSearch && shopSupplierId) {
          const supplierIndex = this.suppliers.findIndex((supplier) => supplier.id === shopSupplierId)
          // Check if we found the supplier
          if (supplierIndex !== -1) {
            this.suppliers[supplierIndex].id = -1
          }
        }

        if (this.validateConfigData(configData)) {
          // Ensure backward compatibility by initializing brandSuppliers if not present
          for (let i = 1; i <= 3; i++) {
            if (!configData[i].hasOwnProperty('brandSuppliers')) {
              configData[i].brandSuppliers = {}
            }
          }
          this.brandsConfig = configData
        } else {
          vxm.alert.warning({
            content: 'Cannot load configuration, try setup a new one',
            title: 'Warning',
          })
          this.resetConfig()
        }
      } else {
        this.resetConfig()
      }
    } catch (err) {
      await vxm.alert.onAxiosError(err, 'Error loading brand configuration')
      this.resetConfig()
    } finally {
      this.configLoading = false
    }
  }

  private getBrandName(brandId: number): string {
    const brand = this.brands.find((b) => b.id === brandId)
    return brand ? brand.name : `Brand ID: ${brandId}`
  }

  private getSupplierName(supplierId: number): string {
    const supplier = this.suppliers.find((s) => s.id === supplierId)
    return supplier ? supplier.name : `Supplier ID: ${supplierId}`
  }

  // Get all currently selected brands across all columns and ranks
  private getAllSelectedBrands(): number[] {
    const selectedBrands: number[] = []

    // Add the currently selected brands in the dropdowns (except for the current column)
    Object.keys(this.selectedBrand).forEach((key) => {
      const columnNum = parseInt(key)
      const brand = this.selectedBrand[columnNum]
      if (brand !== null) {
        selectedBrands.push(brand)
      }
    })

    // Add all brands that are already added to columns
    for (let i = 1; i <= 3; i++) {
      const column = this.brandsConfig[i]
      Object.values(column.rank).forEach((brands) => {
        brands.forEach((brandId) => {
          selectedBrands.push(brandId)
        })
      })
    }

    return selectedBrands
  }

  // Get available brands for a specific column dropdown
  private getAvailableBrandsForColumn(columnNum: number): Brand[] {
    // Get all selected brands except the one currently selected in this dropdown
    const selectedBrands = this.getAllSelectedBrands()
    const currentSelection = this.selectedBrand[columnNum]

    // Filter out brands that are already selected in other dropdowns or columns
    return this.brands.filter((brand) => {
      // Include the current selection for this dropdown
      if (currentSelection === brand.id) {
        return true
      }

      // Check if this brand is already selected elsewhere
      return !selectedBrands.includes(brand.id)
    })
  }

  // Helper method to create a unique key for selected supplier state
  private getSupplierKey(columnNum: number, rank: string, brandId: number): string {
    return `${columnNum}-${rank}-${brandId}`
  }

  // Helper method to create a unique key for brand suppliers in config
  private getBrandSupplierKey(rank: string, brandId: number): string {
    return `${rank}-${brandId}`
  }

  // Get the suppliers for a specific brand
  private getBrandSuppliers(columnNum: number, rank: string, brandId: number): number[] {
    const key = this.getBrandSupplierKey(rank, brandId)
    if (!this.brandsConfig[columnNum].brandSuppliers[key]) {
      this.$set(this.brandsConfig[columnNum].brandSuppliers, key, [])
    }
    return this.brandsConfig[columnNum].brandSuppliers[key]
  }

  // Get available suppliers for a brand that aren't already assigned
  private getAvailableSuppliersForBrand(columnNum: number, rank: string, brandId: number): Supplier[] {
    const assignedSuppliers = this.getBrandSuppliers(columnNum, rank, brandId)
    const key = this.getSupplierKey(columnNum, rank, brandId)
    const currentSelection = this.selectedSupplier[key]

    return this.suppliers.filter((supplier) => {
      // Include current selection
      if (currentSelection === supplier.id) {
        return true
      }
      // Exclude already assigned suppliers
      return !assignedSuppliers.includes(supplier.id)
    })
  }

  // Count the number of ranks in a column
  private countRanksInColumn(columnNum: number): number {
    return Object.keys(this.brandsConfig[columnNum].rank).length
  }

  // Check if brand can be added to a rank
  private canAddBrandToRank(columnNum: number, rank: string): boolean {
    // Check if the maximum number of ranks has been reached
    if (this.countRanksInColumn(columnNum) >= this.MAX_RANKS && !this.brandsConfig[columnNum].rank[rank]) {
      return false
    }

    // Check if the maximum number of brands for this rank has been reached
    if (
      this.brandsConfig[columnNum].rank[rank] &&
      this.brandsConfig[columnNum].rank[rank].length >= this.MAX_BRANDS_PER_RANK
    ) {
      return false
    }

    return true
  }

  // Check if supplier can be added to a brand
  private canAddSupplierToBrand(columnNum: number, rank: string, brandId: number): boolean {
    const suppliers = this.getBrandSuppliers(columnNum, rank, brandId)
    return suppliers.length < this.MAX_SUPPLIERS
  }

  private addBrandToColumn(columnNum: number) {
    const brandId = this.selectedBrand[columnNum]
    const rank = this.selectedRank[columnNum]

    if (!brandId || !rank) return

    // Check if we can add this brand to the rank
    if (!this.canAddBrandToRank(columnNum, rank)) {
      vxm.alert.warning({
        content: 'Maximum limit reached for this rank or column.',
        title: 'Warning',
      })
      return
    }

    // Ensure the rank exists in the configuration
    if (!this.brandsConfig[columnNum].rank[rank]) {
      this.$set(this.brandsConfig[columnNum].rank, rank, [])
    }

    // Check if brand is already in this rank
    if (!this.brandsConfig[columnNum].rank[rank].includes(brandId)) {
      this.brandsConfig[columnNum].rank[rank].push(brandId)

      // Initialize an empty suppliers array for this brand
      const key = this.getBrandSupplierKey(rank, brandId)
      this.$set(this.brandsConfig[columnNum].brandSuppliers, key, [])
    }

    // Reset selection fields
    this.selectedBrand[columnNum] = null
    this.selectedRank[columnNum] = '1'
  }

  private removeBrand(columnNum: number, rank: string, brandId: number) {
    const brandIndex = this.brandsConfig[columnNum].rank[rank].indexOf(brandId)
    if (brandIndex >= 0) {
      this.brandsConfig[columnNum].rank[rank].splice(brandIndex, 1)

      // Remove the suppliers associated with this brand
      const key = this.getBrandSupplierKey(rank, brandId)
      this.$delete(this.brandsConfig[columnNum].brandSuppliers, key)

      // Remove the rank if empty
      if (this.brandsConfig[columnNum].rank[rank].length === 0) {
        this.$delete(this.brandsConfig[columnNum].rank, rank)
      }
    }
  }

  private addSupplierToBrand(columnNum: number, rank: string, brandId: number) {
    const key = this.getSupplierKey(columnNum, rank, brandId)
    const supplierId = this.selectedSupplier[key]

    if (!supplierId) return

    // Check if we can add more suppliers
    if (!this.canAddSupplierToBrand(columnNum, rank, brandId)) {
      vxm.alert.warning({
        content: `Maximum of ${this.MAX_SUPPLIERS} suppliers per brand reached.`,
        title: 'Warning',
      })
      return
    }

    const brandSupplierKey = this.getBrandSupplierKey(rank, brandId)

    // Ensure the suppliers array exists
    if (!this.brandsConfig[columnNum].brandSuppliers[brandSupplierKey]) {
      this.$set(this.brandsConfig[columnNum].brandSuppliers, brandSupplierKey, [])
    }

    // Add supplier if not already added
    if (!this.brandsConfig[columnNum].brandSuppliers[brandSupplierKey].includes(supplierId)) {
      this.brandsConfig[columnNum].brandSuppliers[brandSupplierKey].push(supplierId)
    }

    // Reset selection
    this.selectedSupplier[key] = null
  }

  private removeSupplier(columnNum: number, rank: string, brandId: number, supplierId: number) {
    const brandSupplierKey = this.getBrandSupplierKey(rank, brandId)
    const supplierList = this.brandsConfig[columnNum].brandSuppliers[brandSupplierKey]

    if (supplierList) {
      const index = supplierList.indexOf(supplierId)
      if (index >= 0) {
        supplierList.splice(index, 1)
      }
    }
  }

  private moveSupplier(
    columnNum: number,
    rank: string,
    brandId: number,
    supplierIndex: number,
    direction: 'up' | 'down',
  ) {
    const brandSupplierKey = this.getBrandSupplierKey(rank, brandId)
    const supplierList = this.brandsConfig[columnNum].brandSuppliers[brandSupplierKey]

    if (!supplierList || supplierList.length < 2) return

    if (direction === 'up' && supplierIndex > 0) {
      // Swap with the item above
      const temp = supplierList[supplierIndex]
      this.$set(supplierList, supplierIndex, supplierList[supplierIndex - 1])
      this.$set(supplierList, supplierIndex - 1, temp)
    } else if (direction === 'down' && supplierIndex < supplierList.length - 1) {
      // Swap with the item below
      const temp = supplierList[supplierIndex]
      this.$set(supplierList, supplierIndex, supplierList[supplierIndex + 1])
      this.$set(supplierList, supplierIndex + 1, temp)
    }
  }

  private async saveConfig() {
    if (!this.selectedShopId) return

    this.saveLoading = true
    try {
      await this.$axios.post(`/v4/site/webshop/top-brands-config/${this.selectedShopId}`, {
        data: JSON.stringify(this.brandsConfig),
      })
      vxm.alert.success({
        content: this.$t('c:common:Success') as string,
        title: this.$t('c:common:Success') as string,
      })
    } catch (err) {
      await vxm.alert.onAxiosError(err, 'Error saving brand configuration')
    } finally {
      this.saveLoading = false
    }
  }

  private validateConfigData(data: any): boolean {
    // Check if data is not null or undefined
    if (!data) return false

    // Check if the required columns exist
    if (!data[1] || !data[2] || !data[3]) return false

    // Check if each column has the required properties
    for (let i = 1; i <= 3; i++) {
      const column = data[i]

      // Check if labels property exists (it can be empty string)
      if (!column.hasOwnProperty('label1')) return false
      if (!column.hasOwnProperty('label2')) return false
      if (!column.hasOwnProperty('placeholder')) return false

      // Check if rank property exists and is an object
      if (!column.hasOwnProperty('rank') || typeof column.rank !== 'object') return false

      // Check each rank entry to ensure it contains arrays of numbers
      for (const rankKey in column.rank) {
        const rankValue = column.rank[rankKey]

        // Check if rankValue is an array
        if (!Array.isArray(rankValue)) return false

        // Check if all elements in the array are numbers
        for (const brandId of rankValue) {
          if (typeof brandId !== 'number') return false
        }
      }

      // brandSuppliers is optional for backward compatibility
      if (column.hasOwnProperty('brandSuppliers') && typeof column.brandSuppliers !== 'object') {
        return false
      }
    }

    return true
  }

  // Enforce soft caps during data loading
  @Watch('brandsConfig', { deep: true })
  private enforceSoftCaps() {
    // For each column
    for (let columnNum = 1; columnNum <= 3; columnNum++) {
      const column = this.brandsConfig[columnNum]

      // Ensure rank count is within limits
      const rankKeys = Object.keys(column.rank)
      if (rankKeys.length > this.MAX_RANKS) {
        // Remove excess ranks (keep first MAX_RANKS)
        const ranksToRemove = rankKeys.slice(this.MAX_RANKS)
        ranksToRemove.forEach((rank) => {
          // Also remove associated supplier data
          column.rank[rank].forEach((brandId) => {
            const key = this.getBrandSupplierKey(rank, brandId)
            this.$delete(column.brandSuppliers, key)
          })
          this.$delete(column.rank, rank)
        })
      }

      // For each rank, ensure brand count is within limits
      Object.keys(column.rank).forEach((rank) => {
        const brands = column.rank[rank]
        if (brands.length > this.MAX_BRANDS_PER_RANK) {
          // Remove excess brands (keep first MAX_BRANDS_PER_RANK)
          const brandsToRemove = brands.slice(this.MAX_BRANDS_PER_RANK)
          brandsToRemove.forEach((brandId) => {
            // Remove the brand
            const brandIndex = brands.indexOf(brandId)
            if (brandIndex >= 0) {
              brands.splice(brandIndex, 1)
            }

            // Remove associated supplier data
            const key = this.getBrandSupplierKey(rank, brandId)
            this.$delete(column.brandSuppliers, key)
          })
        }
      })

      // For each brand, ensure supplier count is within limits
      Object.keys(column.brandSuppliers).forEach((key) => {
        const suppliers = column.brandSuppliers[key]
        if (suppliers && suppliers.length > this.MAX_SUPPLIERS) {
          // Trim the suppliers array (keep first MAX_SUPPLIERS)
          column.brandSuppliers[key] = suppliers.slice(0, this.MAX_SUPPLIERS)
        }
      })
    }
  }
}
